/**
 * ChartFactory - Centralized factory for creating Highcharts gauges
 *
 * Reduces code duplication by providing consistent gauge creation methods.
 * All gauges share common configuration with customizable options.
 */

/**
 * Base configuration for all gauge charts
 */
function getBaseGaugeOptions() {
  return {
    chart: {
      type: 'solidgauge',
      animation: {
        duration: 1500,
      },
      height: '55%', // Semi-circle needs ~55% of width as height
      spacingTop: 0,
      spacingBottom: 0,
      events: {
        render: function () {
          // Safety checks
          if (!this.series || !this.series[0] || !this.series[0].points || !this.series[0].points[0]) {
            return;
          }

          const point = this.series[0].points[0];
          const pb = this.yAxis[0].plotLinesAndBands;

          if (pb.length <= 1 || typeof point.y === 'undefined') {
            return;
          }

          let fcolor = '#000000';
          pb.forEach((p) => {
            if (point.y >= p.options.from && point.y < p.options.to) {
              fcolor = p.options.color;
            }
          });

          if (point.graphic) {
            point.graphic.attr({
              fill: fcolor,
            });
          }
        },
      },
    },
    credits: {
      enabled: false,
    },
    accessibility: {
      enabled: false,
    },
    title: {
      text: null,
      style: {
        fontSize: '30px',
      },
      floating: true,
      y: 0,
    },
    pane: {
      center: ['50%', '88%'],
      size: '170%',
      startAngle: -90,
      endAngle: 90,
      background: {
        borderRadius: 5,
        innerRadius: '60%',
        outerRadius: '100%',
        shape: 'arc',
        backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--chart-bg').trim() || '#ffffff',
        borderColor: getComputedStyle(document.documentElement).getPropertyValue('--border-color').trim() || '#cccccc',
      },
    },
    exporting: {
      enabled: false,
    },
    tooltip: {
      enabled: false,
    },
    yAxis: {
      lineWidth: 0,
      tickWidth: 0,
      minorTickInterval: null,
      tickAmount: 1,
      labels: {
        enabled: false,
      },
      tickInterval: 2,
      endOnTick: false,
      startOnTick: false,
      alignTicks: false,
    },
    plotOptions: {
      solidgauge: {
        borderRadius: 3,
        dataLabels: {
          y: 0,
          borderWidth: 0,
          useHTML: true,
        },
      },
    },
  };
}

/**
 * Create a standard semi-circle gauge
 *
 * @param {string} containerId - DOM element ID for the chart
 * @param {string} title - Chart title
 * @param {number} value - Initial value
 * @param {object} options - Custom options to merge
 * @returns {Highcharts.Chart} The created chart instance
 */
export function createGauge(containerId, title, value, options = {}) {
  const baseOptions = getBaseGaugeOptions();

  const chartOptions = Highcharts.merge(baseOptions, {
    title: {
      text: title,
    },
    series: [{
      name: title,
      data: [value],
    }],
  }, options);

  return Highcharts.chart(containerId, chartOptions);
}

/**
 * Create a gauge with min/max gradient colors
 *
 * @param {string} containerId - DOM element ID
 * @param {string} title - Chart title
 * @param {number} value - Initial value
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @param {string} minColor - Color at minimum
 * @param {string} maxColor - Color at maximum
 * @param {string} unit - Unit label (e.g., 'mph', 'in')
 * @param {number} decimals - Number of decimal places
 * @returns {Highcharts.Chart}
 */
export function createGradientGauge(containerId, title, value, min, max, minColor, maxColor, unit = '', decimals = 0) {
  return createGauge(containerId, title, value, {
    yAxis: {
      min: min,
      max: max,
      minColor: minColor,
      maxColor: maxColor,
    },
    series: [{
      data: [value],
      dataLabels: {
        format:
          '<div style="text-align:center">' +
          `<span style="font-size:40px">{y:,.${decimals}f}</span><br/>` +
          `<span style="font-size:18px;opacity:0.4">${unit}</span>` +
          '</div>',
      },
    }],
  });
}

/**
 * Create a gauge with plot bands (colored sections)
 *
 * @param {string} containerId - DOM element ID
 * @param {string} title - Chart title
 * @param {number} value - Initial value
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @param {Array} plotBands - Array of band objects {from, to, color}
 * @param {string} unit - Unit label
 * @param {number} decimals - Decimal places
 * @returns {Highcharts.Chart}
 */
export function createBandedGauge(containerId, title, value, min, max, plotBands, unit = '', decimals = 0) {
  const hasTitle = title && title.length > 0;
  const formattedBands = plotBands.map(band => ({
    from: band.from,
    to: band.to,
    color: band.color,
    outerRadius: '105%',
    thickness: '5%',
  }));

  return createGauge(containerId, title, value, {
    chart: {
      spacingTop: hasTitle ? 10 : 0,
    },
    title: {
      y: hasTitle ? 20 : 0,
    },
    pane: {
      center: hasTitle ? ['50%', '100%'] : ['50%', '88%'],
      size: hasTitle ? '150%' : '170%',
    },
    yAxis: {
      min: min,
      max: max,
      plotBands: formattedBands,
    },
    series: [{
      data: [value],
      dataLabels: {
        format:
          '<div style="text-align:center">' +
          `<span style="font-size:40px">{y:,.${decimals}f}</span><br/>` +
          `<span style="font-size:18px;opacity:0.4">${unit}</span>` +
          '</div>',
      },
    }],
  });
}

/**
 * Create a full-circle gauge (360 degrees)
 *
 * @param {string} containerId - DOM element ID
 * @param {string} title - Chart title
 * @param {number} value - Initial value
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @param {string} unit - Unit label
 * @param {number} decimals - Decimal places
 * @returns {Highcharts.Chart}
 */
export function createFullCircleGauge(containerId, title, value, min, max, unit = '', decimals = 0, colorStops = null) {
  const hasTitle = title && title.length > 0;
  // Default blue gradient for duration-type gauges
  const defaultStops = [
    [0.0, '#a8d4ff'],
    [0.5, '#4a9eff'],
    [1.0, '#0066cc'],
  ];
  return createGauge(containerId, title, value, {
    chart: {
      height: '100%',
      width: null,
      spacingTop: 0,
      spacingBottom: 0,
      spacingLeft: 0,
      spacingRight: 0,
      margin: hasTitle ? [15, 10, 15, 10] : [0, 0, 0, 0],
    },
    title: {
      floating: false,
      y: hasTitle ? 18 : 0,
      style: {
        fontSize: '18px',
      },
    },
    pane: {
      center: ['50%', '50%'],
      size: hasTitle ? '70%' : '95%',
      startAngle: -180,
      endAngle: 180,
      background: {
        borderRadius: 5,
        innerRadius: '60%',
        outerRadius: '100%',
        shape: 'arc',
        backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--chart-bg').trim() || '#ffffff',
        borderColor: getComputedStyle(document.documentElement).getPropertyValue('--border-color').trim() || '#cccccc',
      },
    },
    yAxis: {
      min: min,
      max: max,
      stops: colorStops || defaultStops,
    },
    series: [{
      data: [value],
      dataLabels: {
        y: hasTitle ? -25 : -30,
        format:
          '<div style="text-align:center;line-height:1.1">' +
          `<span style="font-size:32px;display:block">{y:,.${decimals}f}</span>` +
          `<span style="font-size:14px;opacity:0.4">${unit}</span>` +
          '</div>',
      },
    }],
  });
}

/**
 * Create a dual-ring gauge (e.g., temperature + dewpoint)
 *
 * @param {string} containerId - DOM element ID
 * @param {string} title - Chart title
 * @param {number} outerValue - Outer ring value
 * @param {number} innerValue - Inner ring value
 * @param {Array} outerStops - Color stops for outer ring
 * @param {Array} innerStops - Color stops for inner ring
 * @returns {Highcharts.Chart}
 */
export function createDualGauge(containerId, title, outerValue, innerValue, outerStops, innerStops) {
  const baseOptions = getBaseGaugeOptions();
  const hasTitle = title && title.length > 0;

  return Highcharts.chart(containerId, Highcharts.merge(baseOptions, {
    chart: {
      spacingTop: hasTitle ? 10 : 0,
    },
    title: {
      text: title,
      y: hasTitle ? 20 : 0,
    },
    yAxis: [{
      tickWidth: 0,
      minorTickInterval: null,
      tickAmount: 1,
      labels: {
        enabled: false,
      },
      tickInterval: 2,
      endOnTick: false,
      startOnTick: false,
      alignTicks: false,
      min: 0,
      max: 100,
      lineWidth: 0,
      tickPositions: [],
      stops: outerStops,
    },
    {
      tickWidth: 0,
      minorTickInterval: null,
      tickAmount: 1,
      labels: {
        enabled: false,
      },
      tickInterval: 2,
      endOnTick: false,
      startOnTick: false,
      alignTicks: false,
      min: 0,
      max: 100,
      lineWidth: 0,
      tickPositions: [],
      stops: innerStops,
    }],
    pane: {
      center: hasTitle ? ['50%', '100%'] : ['50%', '88%'],
      size: hasTitle ? '150%' : '170%',
      background: [{
        outerRadius: '100%',
        innerRadius: '76%',
        shape: 'arc',
        backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--chart-bg').trim() || '#ffffff',
        borderColor: getComputedStyle(document.documentElement).getPropertyValue('--border-color').trim() || '#cccccc',
      }, {
        outerRadius: '75%',
        innerRadius: '49%',
        shape: 'arc',
        backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--chart-bg').trim() || '#ffffff',
        borderColor: getComputedStyle(document.documentElement).getPropertyValue('--border-color').trim() || '#cccccc',
      }],
    },
    series: [{
      id: 'temperature',
      yAxis: 0,
      data: [{
        radius: '100%',
        innerRadius: '76%',
        y: outerValue,
      }],
      dataLabels: {
        y: -5,
        format:
          '<div style="text-align:center">' +
          '<span style="font-size:36px">{y:,.0f}</span>' +
          '<span style="font-size:14px;opacity:0.4;margin-left:2px">°F</span>' +
          '</div>',
      },
    }, {
      id: 'dewpoint',
      yAxis: 1,
      data: [{
        radius: '75%',
        innerRadius: '49%',
        y: innerValue,
      }],
      dataLabels: {
        enabled: false,
      },
    }],
  }));
}

/**
 * Create a gauge with a plot line (e.g., peak gust indicator)
 *
 * @param {string} containerId - DOM element ID
 * @param {string} title - Chart title
 * @param {number} value - Current value
 * @param {number} plotLineValue - Plot line value
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @param {string} minColor - Gradient start color
 * @param {string} maxColor - Gradient end color
 * @param {string} unit - Unit label
 * @returns {Highcharts.Chart}
 */
export function createGaugeWithPlotLine(containerId, title, value, plotLineValue, min, max, minColor, maxColor, unit = '') {
  return createGauge(containerId, title, value, {
    yAxis: {
      min: min,
      max: max,
      minColor: minColor,
      maxColor: maxColor,
      plotLines: [{
        color: 'red',
        width: 1,
        value: plotLineValue,
        dashStyle: 'Dash',
        label: {
          text: plotLineValue,
          align: 'left',
          x: -8,
          style: {
            fontSize: '0.7em',
            fontWeight: 'bold',
          },
        },
      }],
    },
    series: [{
      data: [value],
      dataLabels: {
        format:
          '<div style="text-align:center">' +
          '<span style="font-size:40px">{y:,.0f}</span><br/>' +
          `<span style="font-size:18px;opacity:0.4">${unit}</span>` +
          '</div>',
      },
    }],
  });
}
